import { X } from 'lucide-react';
import { clsx } from 'clsx';

type BadgeType = 'role' | 'granted' | 'denied';

interface PermissionBadgeProps {
    label: string;
    description?: string;
    type: BadgeType;
    onRemove?: () => void;
    size?: 'sm' | 'md';
}

const badgeConfig: Record<BadgeType, { bg: string; text: string; border: string; dot: string; label: string }> = {
    role: {
        bg: 'bg-slate-50',
        text: 'text-slate-600',
        border: 'border-slate-200',
        dot: 'bg-slate-400',
        label: 'From role',
    },
    granted: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500',
        label: 'Granted',
    },
    denied: {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-200',
        dot: 'bg-rose-500',
        label: 'Denied',
    },
};

/**
 * PermissionBadge — shows a single permission with its source/status.
 *
 * - Green (granted): Explicitly granted to this user
 * - Red (denied): Explicitly denied, even if roles provide it
 * - Gray (role): Inherited from assigned role(s)
 *
 * Click the X button to remove an override (revert to role-based).
 */
export function PermissionBadge({
    label,
    description,
    type,
    onRemove,
    size = 'md',
}: PermissionBadgeProps) {
    const config = badgeConfig[type];
    const isSmall = size === 'sm';

    return (
        <div
            className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-all',
                config.bg,
                config.text,
                config.border,
                isSmall ? 'text-xs' : 'text-sm'
            )}
            title={description || label}
        >
            {/* Status dot */}
            <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', config.dot)} />

            {/* Label */}
            <span className="font-medium capitalize">{label.replace(/_/g, ' ')}</span>

            {/* Type indicator (small) */}
            {!isSmall && (
                <span className={clsx('text-[10px] opacity-60 ml-0.5')}>
                    · {config.label}
                </span>
            )}

            {/* Remove button (only for overrides) */}
            {onRemove && type !== 'role' && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className={clsx(
                        'ml-1 p-0.5 rounded hover:bg-black/5 transition-colors flex-shrink-0',
                        config.text
                    )}
                    title={`Remove ${type === 'granted' ? 'grant' : 'deny'} override`}
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
}